/**
 * DatenschutzPage.jsx – Datenschutzerklärung gemäß DSGVO Art. 13/14
 * Öffentlich zugänglich, kein Login nötig.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Milk, ShieldCheck } from "lucide-react";

export default function DatenschutzPage() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/impressum")
      .then((r) => setM(r.data))
      .catch(() => setM({ name: "Molkerei Seifried GmbH" }));
  }, []);

  const kontaktLine = m?.email
    ? <a href={`mailto:${m.email}`} className="text-primary hover:underline">{m.email}</a>
    : "(E-Mail bitte im Impressum hinterlegen)";

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Milk className="h-5 w-5" />
            </div>
            <span className="font-semibold">seifried.app</span>
          </div>
          <Button variant="ghost" size="sm" asChild data-testid="datenschutz-back-btn">
            <Link to="/login"><ArrowLeft className="h-4 w-4 mr-1.5" /> Zurück</Link>
          </Button>
        </div>

        <Card data-testid="datenschutz-page">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Datenschutzerklärung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm leading-relaxed">

            <section>
              <p className="text-xs text-muted-foreground">
                Stand: {new Date().toLocaleDateString("de-AT", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">1. Verantwortlicher</h2>
              <p>Verantwortlich für die Datenverarbeitung im Sinne der DSGVO ist:</p>
              <p className="mt-2 font-medium">{m?.name || "Molkerei Seifried GmbH"}</p>
              {m?.strasse && <p>{m.strasse}</p>}
              <p>{m?.plz} {m?.ort}</p>
              <p>{m?.land || "Österreich"}</p>
              <p className="mt-2">Kontakt: {kontaktLine}</p>
              {m?.telefon && <p>Telefon: {m.telefon}</p>}
              {m?.geschaeftsfuehrer && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Geschäftsführung: {m.geschaeftsfuehrer}
                </p>
              )}
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">2. Zweck und Rechtsgrundlage der Verarbeitung</h2>
              <p>
                Die Anwendung <strong>seifried.app</strong> dient der elektronischen Milchgeldabrechnung und
                dem Lieferantenportal für Milcherzeuger unserer Molkerei. Personenbezogene Daten werden
                ausschließlich zur Erfüllung des Milchliefervertrags (Art. 6 Abs. 1 lit. b DSGVO), zur Erfüllung
                gesetzlicher Pflichten (Art. 6 Abs. 1 lit. c DSGVO, z. B. Milchgüte­verordnung, LMSVG) sowie
                aufgrund berechtigten Interesses am ordnungsgemäßen Betrieb der Anwendung (Art. 6 Abs. 1 lit. f
                DSGVO) verarbeitet.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">3. Verarbeitete Datenkategorien</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Stammdaten des Lieferanten: Liefernummer, Betriebsname, Adresse, Kontakt, Bankverbindung</li>
                <li>Betriebsdaten: Kuhbestand, Milchsorte (konventionell/bio), Tour-Zuordnung, Gruppe</li>
                <li>Liefer- und Qualitätsdaten: Liefermengen, Temperatur, Probennummern, Fett, Eiweiß,
                    Zellzahl, Keimzahl, Hemmstoff, Gefrierpunkt, Harnstoff</li>
                <li>Abrechnungsdaten: Monatsabrechnungen, Preisberechnung, Auszahlungsbeträge, Steuerdaten</li>
                <li>Benutzerkonto: E-Mail-Adresse, Name, verschlüsseltes Passwort, Rolle, Login-Zeitpunkt</li>
                <li>Technische Daten: Session-Token, User-Agent, für Audit-Log notwendige Zeitstempel</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">4. Speicherdauer</h2>
              <p>
                Personenbezogene Daten werden für die Dauer des Milchliefervertrags sowie darüber hinaus
                für die gesetzlichen Aufbewahrungsfristen (in der Regel 7 Jahre gemäß § 132 BAO) gespeichert.
                Login- und Audit-Log-Daten werden für maximal 24 Monate aufbewahrt, sofern nicht längere
                gesetzliche Fristen greifen.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">5. Empfänger &amp; Auftragsverarbeiter</h2>
              <p>Daten werden ausschließlich an folgende Empfänger übermittelt:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Steuerberater und Buchhaltung (im Rahmen der gesetzlichen Meldepflichten)</li>
                <li>Milchprüfring/Labor (bei externer Analyse von Milchproben)</li>
                <li>Hosting-Provider innerhalb der EU (technischer Betrieb der Anwendung)</li>
              </ul>
              <p className="mt-2">
                Eine Übermittlung in Drittländer außerhalb des Europäischen Wirtschaftsraums (z. B. USA)
                <strong> findet nicht statt</strong>. Es werden keine Analyse-, Tracking- oder Werbe-Dienste
                Dritter (z. B. Google Analytics, PostHog, Meta) eingesetzt.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">6. Externe Ressourcen</h2>
              <p>
                Die Anwendung lädt <strong>keine</strong> externen Ressourcen (Fonts, Skripte, Analyse-Tools)
                nach. Schriftarten werden lokal ausgeliefert, Kartenkacheln stammen vom internen OSM-Tileserver
                der Molkerei. Es werden keine Cookies zu Werbe- oder Analysezwecken gesetzt. Für die
                Benutzeranmeldung wird ein technisch notwendiges Session-Token im Browser-Speicher abgelegt.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">7. Rechte der betroffenen Personen</h2>
              <p>Ihnen stehen nach der DSGVO folgende Rechte zu:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Recht auf Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
                <li>Recht auf Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
                <li>Recht auf Löschung (Art. 17 DSGVO) – soweit gesetzliche Aufbewahrungsfristen dem nicht
                    entgegenstehen</li>
                <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
                <li>Widerspruchsrecht (Art. 21 DSGVO)</li>
                <li>Recht auf Beschwerde bei der Aufsichtsbehörde (Art. 77 DSGVO)</li>
              </ul>
              <p className="mt-2">
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte formlos an {kontaktLine}.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">8. Aufsichtsbehörde</h2>
              <p>
                Beschwerden können an die österreichische Datenschutzbehörde gerichtet werden:<br />
                Barichgasse 40–42, 1030 Wien, <a href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.dsb.gv.at</a>
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">9. Sicherheit</h2>
              <p>
                Die Anwendung überträgt sämtliche Daten ausschließlich über TLS-verschlüsselte Verbindungen
                (HTTPS). Passwörter werden nur als Salted-Hash gespeichert. Der Zugriff auf Daten ist über
                Rollen- und Berechtigungssystem streng eingeschränkt. Sensible Aktionen werden im Audit-Log
                nachvollziehbar protokolliert.
              </p>
            </section>

            <p className="text-xs text-muted-foreground pt-4 border-t">
              Siehe auch: <Link to="/impressum" className="text-primary hover:underline">Impressum</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
